import type { CollectionModelField } from '../collection.field';
import { CollectionModelFieldNestedList } from '../fields/nested.list.field';
import type { Log } from '../../../log';
import { CollectionModelFieldSelectList } from '../fields/select.list.field';

export interface IkeCollectionUpgradeActionReplace {
	action: 'replace';
	fieldId: string;
	targetFieldId: string;
	values: {
		[key: string]: string | string[];
	};
}

export interface IkeCollectionUpgradeActionDrop {
	action: 'drop';
	fieldId: string;
}

export interface IkeCollectionUpgradeActionMap {
	action: 'map';
	fieldId: string;
	targetFieldId: string;
}

export type IkeCollectionMigrationAction =
	| IkeCollectionUpgradeActionReplace
	| IkeCollectionUpgradeActionDrop
	| IkeCollectionUpgradeActionMap;

export class IkeCollectionUpgradeActionFactory {
	static createAction(action: IkeCollectionMigrationAction): IkeCollectionUpgradeRunner {
		switch (action.action) {
			case 'map':
				return new ActionMap(action);
			case 'drop':
				return new ActionDrop(action);
			case 'replace':
				return new ActionReplace(action);
			default:
				throw new Error('Invalid action type ' + action['action']);
		}
	}
}

/** Rule for processing a field from one form to another */
export interface IkeCollectionUpgradeRunner {
	/** Short rule name */
	reason: string;
	/** Target field to apply  */
	targetFieldId: string;
	/** Whether the target field needs to exist */
	requiresTargetField?: boolean;
	process?(oldField: CollectionModelField, newField: CollectionModelField, log: Log): boolean;
}

export class ActionDrop implements IkeCollectionUpgradeRunner {
	reason = 'drop';
	action: IkeCollectionUpgradeActionDrop;
	targetFieldId: string = null;
	constructor(action: IkeCollectionUpgradeActionDrop) {
		this.action = action;
	}

	process(oldField: CollectionModelField, newField: CollectionModelField, log: Log) {
		newField.Mutator.clear();
		return true;
	}
}

export class ActionMap implements IkeCollectionUpgradeRunner {
	reason = 'map';
	action: IkeCollectionUpgradeActionMap;
	targetFieldId: string;
	requiresTargetField = true;
	constructor(action: IkeCollectionUpgradeActionMap) {
		this.action = action;
		this.targetFieldId = action.targetFieldId;
	}
}

export class ActionReplace implements IkeCollectionUpgradeRunner {
	reason = 'replace';
	action: IkeCollectionUpgradeActionReplace;
	targetFieldId: string;
	requiresTargetField = true;
	constructor(action: IkeCollectionUpgradeActionReplace) {
		this.action = action;
		this.targetFieldId = action.targetFieldId;
	}

	process(oldField: CollectionModelField, newField: CollectionModelField, log: Log) {
		// The values already aligned no need to transform
		if (oldField.displayValue === newField.displayValue) {
			return true;
		}

		if (
			CollectionModelFieldNestedList.isNestedListField(oldField) &&
			CollectionModelFieldNestedList.isNestedListField(newField)
		) {
			return this.processNestedList(oldField, newField, log);
		}

		if (
			CollectionModelFieldSelectList.isSelectListField(oldField) &&
			CollectionModelFieldSelectList.isSelectListField(newField)
		) {
			return this.processSelectList(oldField, newField, log);
		}

		log.error(
			{ fieldType: newField.schema.type },
			`ActionReplace: Unsupported field type for replace '${newField.schema.type}'`
		);
		return false;
	}

	private processNestedList(
		oldField: CollectionModelFieldNestedList,
		newField: CollectionModelFieldNestedList,
		log: Log
	): boolean {
		const newValue = this.action.values[oldField.displayValue];

		if (!Array.isArray(newValue)) {
			return false;
		}

		// Unset field
		if (newValue.length === 0) {
			newField.Mutator.manual(null);
			log.info(
				{ oldValue: oldField.displayValue },
				`ActionReplace: Dropped value ${CollectionModelFieldNestedList.TYPE}`
			);
			return true;
		}

		newField.Mutator.manual(newValue);
		if (!newField.hasValue()) {
			log.error(
				{ oldValue: oldField.displayValue, newValue: newValue.join(' > ') },
				`ActionReplace: Failed to find match for ${CollectionModelFieldNestedList.TYPE}`
			);
			return false;
		}

		return true;
	}

	private processSelectList(
		oldField: CollectionModelFieldSelectList,
		newField: CollectionModelFieldSelectList,
		log: Log
	): boolean {
		const newValue = this.action.values[oldField.displayValue];
		if (typeof newValue !== 'string') {
			return false;
		}

		// Unset field
		if (newValue.length === 0) {
			newField.Mutator.manual(null);
			log.info(
				{ oldValue: oldField.displayValue },
				`ActionReplace: Dropped value ${CollectionModelFieldNestedList.TYPE}`
			);
			return true;
		}

		newField.Mutator.manual(newValue);
		if (!newField.hasValue()) {
			log.error(
				{ oldValue: oldField.displayValue, newValue },
				`ActionReplace: Failed to find match for ${CollectionModelFieldSelectList.TYPE}`
			);
			return false;
		}

		return true;
	}
}
